import { useCallback, useEffect, useMemo, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { deleteChecklistNative } from '../../services/checklistWriter'
import { binChecklist } from '../../services/elementRestructureService'
import { recordWorkspaceActivity } from '../../services/activityService'
import { updateChecklistFields } from '../../services/checklistMutationService'
import {
  queueChecklistCanvasProjectionDelete,
  queueChecklistCanvasProjectionUpsert,
} from '../../services/checklistCanvasProjectionService'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import type { useAuth } from '../../context/useAuth'
import type { ChecklistItem as BoardChecklistItem, TimelineBoardKey } from '../../types/schema'
import { CHECKLIST_CANVAS_LAYOUT_VERSION as WORKSPACE_CANVAS_LAYOUT_VERSION } from '../../types/checklistCanvas'
import {
  buildChecklistBookLayoutEdit,
  buildReciprocalDependencyLinks,
} from '../../utils/checklistCanvasSaveGraph'
import { partitionChecklistBoardSaveNodes } from '../../utils/checklistBoardAuthority'
import { invalidateChecklistDefinitionListCache } from '../../services/checklistReadCache'
import { isGateNode } from './boardItem'
import {
  checklistBoardSessionSnapshotCache,
  cloneBoardSnapshot,
} from './sessionSnapshot'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'

export function useBoardSession({
  undoStackRef,
  nodes,
  setCanUndoBoard,
  setNodes,
  setIsBoardDirty,
  baselineNodesRef,
  setOptimisticPositions,
  pendingDeletionsRef,
  setFlashMessage,
  boardSessionSnapshotKey,
  currentUser,
  currentDisplayName,
  item,
  recurringLens,
  boardDocumentIdsRef,
  canEditCanvas,
  canDeleteChecklist,
  canCreateChecklist,
  isDebugCanvas,
  optimisticPositions,
  optimisticWeights,
  dialogBusyAction,
  setGateDialogError,
  setOpenGatesDialog,
  pendingCloseActionRef,
  setDialogBusyAction,
  setCloseDialogError,
  onClose,
  setCloseBoardDialogOpen,
  isBoardDirty,
  breadcrumbChain,
  dashboardChecklistRootIds,
  dashboardElements,
}: {
  undoStackRef: MutableRefObject<BoardChecklistItem[][]>
  nodes: BoardChecklistItem[]
  setCanUndoBoard: Dispatch<SetStateAction<boolean>>
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  setIsBoardDirty: Dispatch<SetStateAction<boolean>>
  baselineNodesRef: MutableRefObject<BoardChecklistItem[]>
  setOptimisticPositions: Dispatch<SetStateAction<Record<string, { x: number; y: number }>>>
  pendingDeletionsRef: MutableRefObject<Set<string>>
  setFlashMessage: Dispatch<SetStateAction<string | null>>
  boardSessionSnapshotKey: string
  currentUser: ReturnType<typeof useAuth>['currentUser']
  currentDisplayName: string
  item: TimelineElement
  recurringLens: ReturnType<typeof useRecurringLens>
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  canEditCanvas: boolean
  canDeleteChecklist: boolean
  canCreateChecklist: boolean
  isDebugCanvas: boolean
  optimisticPositions: Record<string, { x: number; y: number }>
  optimisticWeights: Record<string, number>
  dialogBusyAction: null | 'close-save' | 'close-discard' | 'gate-save'
  setGateDialogError: Dispatch<SetStateAction<string | null>>
  setOpenGatesDialog: Dispatch<SetStateAction<{ intent: 'save' | 'close' } | null>>
  pendingCloseActionRef: MutableRefObject<(() => void) | null>
  setDialogBusyAction: Dispatch<SetStateAction<null | 'close-save' | 'close-discard' | 'gate-save'>>
  setCloseDialogError: Dispatch<SetStateAction<string | null>>
  onClose: () => void
  setCloseBoardDialogOpen: Dispatch<SetStateAction<boolean>>
  isBoardDirty: boolean
  breadcrumbChain: TimelineElement[]
  dashboardChecklistRootIds: Set<string>
  dashboardElements: TimelineElement[]
}) {
  const { timeZone } = useWorkspaceDay(item.workspaceId)

  // ---- Local-first board mutations (defer position + connection writes) ----

  // Snapshot the current board into the undo stack (max 5) before a local edit.
  const pushBoardUndo = useCallback(() => {
    undoStackRef.current = [...undoStackRef.current, nodes].slice(-5)
    setCanUndoBoard(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setCanUndoBoard, undoStackRef are useRef/useState identities passed from the Surface (stable)
  }, [nodes])

  // Apply a local-only node change: snapshot for undo, update state, mark dirty.
  const commitLocalNodes = useCallback((updater: (current: BoardChecklistItem[]) => BoardChecklistItem[]) => {
    pushBoardUndo()
    setNodes((current) => updater(current))
    setIsBoardDirty(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setIsBoardDirty, setNodes are useRef/useState identities passed from the Surface (stable)
  }, [pushBoardUndo])

  const restoreBoard = useCallback(() => {
    setNodes(baselineNodesRef.current)
    setOptimisticPositions({})
    undoStackRef.current = []
    pendingDeletionsRef.current.clear()
    setCanUndoBoard(false)
    setIsBoardDirty(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, pendingDeletionsRef, setCanUndoBoard, setIsBoardDirty, setNodes, setOptimisticPositions, undoStackRef are useRef/useState identities passed from the Surface (stable)
  }, [])

  const undoBoard = useCallback(() => {
    const stack = undoStackRef.current
    if (stack.length === 0) return
    const previous = stack[stack.length - 1]
    undoStackRef.current = stack.slice(0, -1)
    setNodes(previous)
    setCanUndoBoard(undoStackRef.current.length > 0)
    setIsBoardDirty(previous !== baselineNodesRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, setCanUndoBoard, setIsBoardDirty, setNodes, undoStackRef are useRef/useState identities passed from the Surface (stable)
  }, [])

  // setFlashMessage is the auto-clearing toast used by showFlash (declared
  // later in this component). Calling it directly here avoids a forward
  // reference while keeping the same visible behaviour.
  const flashLocal = useCallback((message: string) => {
    setFlashMessage(message)
    window.setTimeout(() => setFlashMessage(null), 1400)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setFlashMessage are useRef/useState identities passed from the Surface (stable)
  }, [])

  const finalizeSavedBoardSnapshot = useCallback((
    saved: BoardChecklistItem[],
    flashMessageText: string | null = 'Saved positions & connections.',
  ) => {
    setNodes(saved)
    setOptimisticPositions({})
    baselineNodesRef.current = saved
    undoStackRef.current = []
    setCanUndoBoard(false)
    setIsBoardDirty(false)
    checklistBoardSessionSnapshotCache.set(boardSessionSnapshotKey, cloneBoardSnapshot(saved))
    if (flashMessageText) flashLocal(flashMessageText)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, setCanUndoBoard, setIsBoardDirty, setNodes, setOptimisticPositions, undoStackRef are useRef/useState identities passed from the Surface (stable)
  }, [boardSessionSnapshotKey, flashLocal])

  const syncChecklistBookLayoutForType = useCallback(async (
    type: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY',
    nodesForType: readonly BoardChecklistItem[],
  ) => {
    if (nodesForType.length === 0) return
    const actor = {
      uid: currentUser?.uid ?? '',
      displayName: currentDisplayName || currentUser?.email || undefined,
    }
    if (!actor.uid) return

    const { bookIdFor } = await import('../../types/checklistBooks')
    const { ensurePageForKey, pageKeyFor } = await import('../../services/checklistBookService')
    const { applyEditToPage, ensureActiveLayoutVersion, getLayoutVersion } = await import('../../services/layoutVersionService')

    const bookId = bookIdFor(item.workspaceId, type)
    const pageKey = recurringLens && type === recurringLens.mode
      ? recurringLens.focusedKey
      : pageKeyFor(type, new Date(), timeZone)
    const page = await ensurePageForKey(bookId, item.workspaceId, type, pageKey, actor)
    const baseLayoutVersionId = page.layoutVersionId
      || await ensureActiveLayoutVersion(bookId, item.workspaceId, actor)
    const existing = await getLayoutVersion(bookId, baseLayoutVersionId).catch(() => null)
    const nextLayout = buildChecklistBookLayoutEdit(existing, nodesForType)
    await applyEditToPage(
      bookId,
      item.workspaceId,
      pageKey,
      nextLayout,
      actor,
    )
  }, [currentDisplayName, currentUser, item.workspaceId, recurringLens, timeZone])

  /**
   * Open-gate detection. A gate is OPEN when it lacks either ≥2 inputs OR
   * doesn't have at least one downstream checklist node referencing it via
   * `prerequisites`. Returns the set of open gate ids in the current local
   * state.
   */
  const getOpenGateIds = useCallback((nodeList: BoardChecklistItem[]) => {
    const open = new Set<string>()
    for (const node of nodeList) {
      if (!isGateNode(node)) continue
      const inputs = node.gateInputIds?.length ?? 0
      const inputsOk = inputs >= 2 && inputs <= 4
      const hasOutput = nodeList.some((candidate) => candidate.prerequisites.includes(node.id))
      if (!inputsOk || !hasOutput) open.add(node.id)
    }
    return open
  }, [])

  const buildOpenGateCleanupSnapshot = useCallback(() => {
    const open = getOpenGateIds(nodes)
    if (open.size === 0) return { cleaned: nodes, persistedDeletionIds: [] as string[] }
    const cleaned = nodes
      .filter((node) => !open.has(node.id))
      .map((node) => ({
        ...node,
        prerequisites: node.prerequisites.filter((id) => !open.has(id)),
        gateInputIds: (node.gateInputIds ?? []).filter((id) => !open.has(id)),
      }))
    const persistedDeletionIds = [...open].filter((id) => boardDocumentIdsRef.current.has(id))
    return { cleaned, persistedDeletionIds }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardDocumentIdsRef are useRef/useState identities passed from the Surface (stable)
  }, [nodes, getOpenGateIds])

  /** Persist the given local node list to Firestore (no open-gate guard).
   * Used by both the normal save path and the open-gate dialog's "Remove all
   * open gates and Save anyways" flow, which feeds the already-cleaned list. */
  const persistBoardSnapshot = useCallback(async (snapshot: BoardChecklistItem[]) => {
    if (!canEditCanvas) {
      flashLocal('You do not have permission to save canvas changes.')
      return false
    }
    const { supported: supportedSnapshot, skippedLegacyIds } = partitionChecklistBoardSaveNodes(
      snapshot,
      boardDocumentIdsRef.current,
    )
    if (skippedLegacyIds.length > 0) {
      console.warn('[ChecklistDrawingBoard] skipping unsupported legacy checklist nodes during save', skippedLegacyIds)
    }
    const batch = writeBatch(firestoreDb)
    const newDocIds = new Set<string>()
    const nodeLookup = new Map(supportedSnapshot.map((node) => [node.id, node]))
    const baselineLookup = new Map(baselineNodesRef.current
      .filter((node) => !skippedLegacyIds.includes(node.id))
      .map((node) => [node.id, node]))
    const dependencyChanges = supportedSnapshot.flatMap((target) => {
      const before = new Set(baselineLookup.get(target.id)?.prerequisites ?? [])
      const after = new Set(target.prerequisites)
      return [
        ...[...after].filter((sourceId) => !before.has(sourceId)).map((sourceId) => ({ kind: 'added', sourceId, target })),
        ...[...before].filter((sourceId) => !after.has(sourceId)).map((sourceId) => ({ kind: 'removed', sourceId, target })),
      ]
    })
    const reciprocalLinks = buildReciprocalDependencyLinks(supportedSnapshot)
    if (isDebugCanvas) {
      const saved = supportedSnapshot.map((node) => ({
        ...node,
        position: optimisticPositions[node.id] ?? node.position,
        dependencyNodeIds: node.prerequisites,
        workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
        blockedByNodeIds: reciprocalLinks.get(node.id)?.blockedBy ?? [],
        isBlockerForNodeIds: reciprocalLinks.get(node.id)?.blockerFor ?? [],
        updatedAt: new Date().toISOString(),
        lastUpdatedBy: currentUser?.uid ?? null,
        lastUpdatedByName: currentDisplayName || currentUser?.email || null,
      }))
      boardDocumentIdsRef.current = new Set(saved.map((node) => node.id))
      pendingDeletionsRef.current.clear()
      finalizeSavedBoardSnapshot(saved)
      return true
    }
    // Drain the pending-deletions queue first — these are previously-persisted
    // ids that were dropped locally (gate deletes, "Remove all open gates and
    // Save anyways" cleanup, etc). Without this they linger in Firestore and
    // re-appear on the next reopen/load.
    const deletionIds: string[] = []
    pendingDeletionsRef.current.forEach((id) => {
      if (boardDocumentIdsRef.current.has(id)) {
        queueChecklistCanvasProjectionDelete(batch, firestoreDb, id)
        deletionIds.push(id)
      }
    })
    if (deletionIds.length > 0 && !canDeleteChecklist) {
      flashLocal('You do not have permission to delete checklist items.')
      return false
    }
    // For EXISTING recurring items (DAILY/WEEKLY) we intentionally split
    // versioned fields (dependencyNodeIds / prerequisites / blockedByNodeIds /
    // isBlockerForNodeIds / gateInputIds) off the batch definition write.
    // Prior behavior clobbered every past/present/future occurrence's
    // inherited deps via the direct `checklists/{id}` update — bypassing the
    // scope dialog and violating the per-instance contract. Those fields now
    // route through `updateChecklistFields` with `scope: 'future'` on the
    // focused occurrence, matching the recurring-detail default at
    // `checklistMutationService.ts:322`. New recurring items still write the
    // full definition once — they have no per-page overrides yet.
    type RecurringVersionedWrite = {
      node: BoardChecklistItem
      links: ReturnType<typeof reciprocalLinks.get>
    }
    const recurringVersionedWrites: RecurringVersionedWrite[] = []
    for (const node of supportedSnapshot) {
      const position = optimisticPositions[node.id] ?? node.position
      const ref = doc(firestoreDb, 'checklists', node.id)
      const links = reciprocalLinks.get(node.id)
      const persistedNode = {
        ...node,
        position,
        dependencyNodeIds: node.prerequisites,
        workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
        board: (item.board ?? node.board ?? 'DASHBOARD') as TimelineBoardKey,
        blockedByNodeIds: links?.blockedBy ?? [],
        isBlockerForNodeIds: links?.blockerFor ?? [],
        blockedByExternal: null,
        blockedByExternalId: null,
        interWorkspaceTargetId: null,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: currentUser?.uid ?? null,
        lastUpdatedByName: currentDisplayName || currentUser?.email || null,
      }
      const isRecurring = node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
      if (!boardDocumentIdsRef.current.has(node.id)) {
        if (!canCreateChecklist) {
          flashLocal('You do not have permission to create checklist items.')
          return false
        }
        batch.set(ref, { ...persistedNode, sourceChecklistId: node.id })
        queueChecklistCanvasProjectionUpsert(batch, firestoreDb, { ...persistedNode, sourceChecklistId: node.id })
        newDocIds.add(node.id)
        continue
      }
      if (isRecurring) {
        // Skip versioned fields — route them below via the book-model.
        batch.update(ref, {
          position,
          workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
          updatedAt: serverTimestamp(),
          lastUpdatedBy: currentUser?.uid ?? null,
          lastUpdatedByName: currentDisplayName || currentUser?.email || null,
        })
        queueChecklistCanvasProjectionUpsert(batch, firestoreDb, persistedNode)
        recurringVersionedWrites.push({ node, links })
        continue
      }
      batch.update(ref, {
        position,
        workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
        dependencyNodeIds: node.prerequisites,
        prerequisites: node.prerequisites,
        blockedByNodeIds: links?.blockedBy ?? [],
        isBlockerForNodeIds: links?.blockerFor ?? [],
        gateInputIds: node.gateInputIds ?? [],
        updatedAt: serverTimestamp(),
        lastUpdatedBy: currentUser?.uid ?? null,
        lastUpdatedByName: currentDisplayName || currentUser?.email || null,
      })
      queueChecklistCanvasProjectionUpsert(batch, firestoreDb, persistedNode)
    }
    try {
      await batch.commit()
      newDocIds.forEach((id) => boardDocumentIdsRef.current.add(id))
      deletionIds.forEach((id) => {
        boardDocumentIdsRef.current.delete(id)
        pendingDeletionsRef.current.delete(id)
      })
      const affectedTypes = new Set<'INDIVIDUAL' | 'DAILY' | 'WEEKLY'>()
      supportedSnapshot.forEach((node) => affectedTypes.add((node.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'))
      deletionIds.forEach((id) => {
        const deletedNode = baselineLookup.get(id)
        affectedTypes.add((deletedNode?.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY')
      })
      affectedTypes.forEach((type) => invalidateChecklistDefinitionListCache(item.workspaceId, type))
      // Book-model cleanup for anything the batch just deleted.
      if (deletionIds.length > 0) {
        void binChecklist(item.workspaceId, deletionIds).catch((err) =>
          console.debug('[board] binChecklist after batch skipped', (err as Error)?.message),
        )
        const actor = { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null }
        void Promise.all(
          deletionIds.map((id) => deleteChecklistNative(id, actor)
            .catch((err) => console.debug('[board] native delete after batch skipped', (err as Error)?.message))),
        )
      }
      const saved = supportedSnapshot.map((node) => ({
        ...node,
        position: optimisticPositions[node.id] ?? node.position,
        dependencyNodeIds: node.prerequisites,
        workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
        blockedByNodeIds: reciprocalLinks.get(node.id)?.blockedBy ?? [],
        isBlockerForNodeIds: reciprocalLinks.get(node.id)?.blockerFor ?? [],
      }))
      finalizeSavedBoardSnapshot(saved, null)
      // Sync the layout version for every type present in this Save — not
      // just the currently-viewed canvas. Prior behavior only bumped the
      // lens' current type; other-type items had their `position` written
      // to the legacy definition but never landed in the book's
      // `layout_versions`, so re-loading the recurring canvas snapped nodes
      // back to their pre-drag positions.
      await Promise.all(Array.from(affectedTypes).map(async (type) => {
        const nodesForType = saved.filter((node) => (node.checklistType ?? 'INDIVIDUAL') === type)
        if (nodesForType.length === 0) return
        try {
          await syncChecklistBookLayoutForType(type, nodesForType)
        } catch (syncError) {
          console.warn('[ChecklistDrawingBoard] layout sync failed after board save', { type, syncError })
        }
      }))
      // Recurring items' versioned fields (deps/wires) route through the
      // book-model with scope='future' anchored on the focused occurrence.
      // Activity emission is suppressed here — the board's per-diff
      // CHECKLIST_DEPENDENCY_CHANGED events below cover the audit trail.
      if (recurringVersionedWrites.length > 0) {
        const mutationActor = {
          id: currentUser?.uid ?? null,
          name: currentDisplayName || currentUser?.email || null,
        }
        await Promise.all(recurringVersionedWrites.map(({ node, links }) => {
          const nodeType = (node.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
          const recurringPageKey = recurringLens && recurringLens.mode === nodeType
            ? recurringLens.focusedKey
            : null
          return updateChecklistFields(firestoreDb, {
            item: node,
            actor: mutationActor,
            pageKey: recurringPageKey,
            emitActivity: false,
            updates: {
              dependencyNodeIds: node.prerequisites,
              prerequisites: node.prerequisites,
              blockedByNodeIds: links?.blockedBy ?? [],
              isBlockerForNodeIds: links?.blockerFor ?? [],
              gateInputIds: node.gateInputIds ?? [],
            },
          }).catch((routeError) => {
            console.warn('[ChecklistDrawingBoard] recurring versioned-field routing failed', {
              checklistId: node.id, type: nodeType, routeError,
            })
          })
        }))
      }
      flashLocal('Saved positions & connections.')
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: item.workspaceId,
        actorId: currentUser?.uid,
        actorName: currentDisplayName || currentUser?.email,
        targetType: 'CANVAS',
        targetId: item.id,
        targetTitle: item.title,
        action: 'CANVAS_SAVE',
        summary: `Saved checklist canvas for ${item.title}`,
        board: item.board,
      }).catch((error) => console.warn('[ChecklistDrawingBoard] activity write failed', error))
      await Promise.all(dependencyChanges.map((change) => {
        const source = nodeLookup.get(change.sourceId) ?? baselineLookup.get(change.sourceId)
        return recordWorkspaceActivity(firestoreDb, {
          workspaceId: item.workspaceId,
          actorId: currentUser?.uid,
          actorName: currentDisplayName || currentUser?.email,
          targetType: 'CHECKLIST',
          targetId: change.target.id,
          targetTitle: change.target.title,
          action: 'CHECKLIST_DEPENDENCY_CHANGED',
          summary: `${change.kind === 'added' ? 'Connected' : 'Disconnected'} blocker ${source?.title ?? change.sourceId}`,
          board: 'DASHBOARD',
          metadata: { kind: change.kind, sourceId: change.sourceId, targetId: change.target.id },
        })
      })).catch((error) => console.warn('[ChecklistDrawingBoard] dependency activity write failed', error))
      return true
    } catch (error) {
      console.error('[ChecklistDrawingBoard] board save failed', error)
      flashLocal('Save failed. Try again.')
      return false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, boardDocumentIdsRef, pendingDeletionsRef are useRef/useState identities passed from the Surface (stable)
  }, [canCreateChecklist, canDeleteChecklist, canEditCanvas, currentDisplayName, currentUser, finalizeSavedBoardSnapshot, flashLocal, isDebugCanvas, item, optimisticPositions, recurringLens, syncChecklistBookLayoutForType])

  const saveBoard = async () => {
    if (!canEditCanvas) return
    if (dialogBusyAction) return
    if (getOpenGateIds(nodes).size > 0) {
      setGateDialogError(null)
      setOpenGatesDialog({ intent: 'save' })
      return
    }
    await persistBoardSnapshot(nodes)
  }

  const completeBoardClose = () => {
    const pendingAction = pendingCloseActionRef.current
    pendingCloseActionRef.current = null
    setDialogBusyAction(null)
    setCloseDialogError(null)
    setGateDialogError(null)
    if (pendingAction) pendingAction()
    else onClose()
  }

  const cancelBoardClose = () => {
    if (dialogBusyAction) return
    pendingCloseActionRef.current = null
    setCloseBoardDialogOpen(false)
    setOpenGatesDialog(null)
    setCloseDialogError(null)
    setGateDialogError(null)
  }

  const handleBoardCloseRequest = (onApproved?: () => void) => {
    if (dialogBusyAction) return
    pendingCloseActionRef.current = onApproved ?? null
    // Priority 1: open gates block both save and close — show the dedicated
    // "You cannot leave a Gate open" dialog. The intent decides what runs
    // after "Remove all open gates and Save anyways" (close, in this case).
    if (canEditCanvas && getOpenGateIds(nodes).size > 0) {
      setCloseDialogError(null)
      setGateDialogError(null)
      setOpenGatesDialog({ intent: 'close' })
      return
    }
    if (canEditCanvas && isBoardDirty) {
      setCloseDialogError(null)
      setGateDialogError(null)
      setCloseBoardDialogOpen(true)
      return
    }
    completeBoardClose()
  }

  const requestBoardNavigation = useCallback((action: () => void) => {
    handleBoardCloseRequest(action)
  }, [handleBoardCloseRequest])

  useEffect(() => {
    const requestClose = (event: Event) => {
      const detail = (event as CustomEvent<{ onApproved?: () => void; handled?: boolean }>).detail
      if (detail && typeof detail === 'object') detail.handled = true
      const onApproved = detail?.onApproved
      handleBoardCloseRequest(onApproved)
    }
    window.addEventListener('timelining:checklist-request-close', requestClose)
    return () => window.removeEventListener('timelining:checklist-request-close', requestClose)
  }, [handleBoardCloseRequest])

  const localReciprocalLinks = useMemo(
    () => buildReciprocalDependencyLinks(nodes),
    [nodes],
  )

  const nodesById = useMemo(() => new Map(nodes.map((node) => {
    const links = localReciprocalLinks.get(node.id)
    return [node.id, {
      ...node,
      dependencyNodeIds: node.prerequisites,
      blockedByNodeIds: links?.blockedBy ?? [],
      isBlockerForNodeIds: links?.blockerFor ?? [],
      weightage: optimisticWeights[node.id] ?? node.weightage,
      position: optimisticPositions[node.id] ?? node.position,
    }] as const
  })), [localReciprocalLinks, nodes, optimisticPositions, optimisticWeights])

  const visibleContainerIds = useMemo(() => {
    const rootContainerId = recurringLens
      ? (breadcrumbChain[breadcrumbChain.length - 1]?.id ?? WORKSPACE_DEFAULT_CHECKLIST_ID)
      : item.id
    if (rootContainerId === WORKSPACE_DEFAULT_CHECKLIST_ID) {
      return new Set([...dashboardChecklistRootIds, ...dashboardElements.map((element) => element.id)])
    }
    const ids = new Set<string>([rootContainerId])
    let added = true
    while (added) {
      added = false
      dashboardElements.forEach((element) => {
        if (element.parentId && ids.has(element.parentId) && !ids.has(element.id)) {
          ids.add(element.id)
          added = true
        }
      })
    }
    return ids
  }, [breadcrumbChain, dashboardChecklistRootIds, dashboardElements, item.id, recurringLens])

  const activeCanvasContainerId = useMemo(
    () => recurringLens
      ? (breadcrumbChain[breadcrumbChain.length - 1]?.id ?? WORKSPACE_DEFAULT_CHECKLIST_ID)
      : item.id,
    [breadcrumbChain, item.id, recurringLens],
  )

  return {
    pushBoardUndo,
    commitLocalNodes,
    restoreBoard,
    undoBoard,
    flashLocal,
    finalizeSavedBoardSnapshot,
    syncChecklistBookLayoutForType,
    getOpenGateIds,
    buildOpenGateCleanupSnapshot,
    persistBoardSnapshot,
    saveBoard,
    completeBoardClose,
    cancelBoardClose,
    handleBoardCloseRequest,
    requestBoardNavigation,
    localReciprocalLinks,
    nodesById,
    visibleContainerIds,
    activeCanvasContainerId,
  }
}
